import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const STATUT_LABEL = { en_cours: 'En cours', gagne: 'Gagné', archive: 'Archivé', perdu: 'Perdu', draft: 'Brouillon' };
const STATUT_COLOR = { en_cours: '#f59e0b', gagne: '#10b981', archive: '#6b7280', perdu: '#ef4444', draft: '#9ca3af' };

const GlobalSearch = ({ compact = true }) => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ devis: [], clients: [], factures: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const isManager = profile?.role === 'manager';

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') { setOpen(false); setQuery(''); } };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults({ devis: [], clients: [], factures: [] }); return; }
    const timer = setTimeout(() => rechercher(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const rechercher = async (q) => {
    if (!user?.id) return;
    setLoading(true);
    const q2 = q.toLowerCase();
    try {
      // DEVIS - recherche par N° ou nom client via ilike Supabase
      let devisQ = supabase.from('quotes')
        .select('id, quote_number, statut, created_at, client_info, total_with_vat, user_id')
        .order('created_at', { ascending: false })
        .limit(500);
      if (!isManager) devisQ = devisQ.eq('user_id', user.id);
      const { data: devisData } = await devisQ;
      // Filtrer aussi par nom client (client_info est JSON)
      const devis = (devisData || []).filter(d =>
        d.quote_number?.toLowerCase().includes(q2) ||
        (d.client_info?.clientName || '').toLowerCase().includes(q2) ||
        (d.client_info?.name || '').toLowerCase().includes(q2) ||
        (d.client_info?.attentionName || '').toLowerCase().includes(q2)
      ).slice(0, 5);

      // CLIENTS
      let clientsQ = supabase.from('clients')
        .select('id, nom, email, mobile, ville')
        .order('created_at', { ascending: false });
      if (!isManager) clientsQ = clientsQ.eq('commerciale_id', user.id);
      const { data: clientsAll } = await clientsQ;
      const clients = (clientsAll || []).filter(c =>
        c.nom?.toLowerCase().includes(q2) ||
        c.email?.toLowerCase().includes(q2) ||
        (c.mobile || '').includes(q)
      ).slice(0, 5);

      // FACTURES
      let facturesQ = supabase.from('invoices')
        .select('id, invoice_number, statut, montant, quote_id, quotes!inner(client_info)')
        .order('created_at', { ascending: false })
        .limit(500);
      const { data: facturesAll } = await facturesQ;
      const factures = (facturesAll || []).filter(f =>
        f.invoice_number?.toLowerCase().includes(q2) ||
        JSON.stringify(f.quotes?.client_info || {}).toLowerCase().includes(q2)
      ).slice(0, 5);

      setResults({ devis, clients, factures });
    } catch(e) { console.error('Recherche globale:', e); }
    setLoading(false);
  };

  const hasResults = results.devis.length + results.clients.length + results.factures.length > 0;

  const btnStyle = {
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
    color: 'white', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer',
    fontSize: '13px', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px'
  };

  return (
    <>
      {compact ? (
        <button onClick={() => setOpen(true)}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          style={btnStyle} title="Recherche globale">🔍</button>
      ) : (
        <div onClick={() => setOpen(true)}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          style={{ ...btnStyle, minWidth: '180px' }}>
          <span>🔍</span>
          <span style={{ color: 'rgba(255,255,255,0.8)' }}>Rechercher...</span>
        </div>
      )}

      {open && (
        <div onClick={e => { if (e.target === e.currentTarget) { setOpen(false); setQuery(''); } }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 9999, paddingTop: '80px', backdropFilter: 'blur(6px)' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '620px', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', overflow: 'hidden' }}>

            {/* Header recherche */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', background: 'linear-gradient(135deg,#1e40af,#3b82f6)' }}>
              <span style={{ fontSize: '20px' }}>🔍</span>
              <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Rechercher un devis, client, facture..."
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: '16px', background: 'transparent', color: 'white', fontFamily: 'inherit' }} />
              {loading && <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>⏳</span>}
              <button onClick={() => { setOpen(false); setQuery(''); }}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Fermer</button>
            </div>

            {/* Resultats */}
            <div style={{ maxHeight: '480px', overflowY: 'auto' }}>
              {!query.trim() && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>🔍</div>
                  <div style={{ fontSize: '14px', fontWeight: 500 }}>Tapez pour rechercher dans vos devis, clients et factures</div>
                  <div style={{ fontSize: '12px', marginTop: '6px' }}>N° devis, nom client, email, N° facture...</div>
                </div>
              )}
              {query.trim() && !hasResults && !loading && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>📭</div>
                  <div style={{ fontSize: '14px' }}>Aucun résultat pour <strong>"{query}"</strong></div>
                </div>
              )}

              {results.devis.length > 0 && (<>
                <div style={{ padding: '8px 20px 6px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>📄 Devis ({results.devis.length})</div>
                {results.devis.map(d => (
                  <div key={d.id} onClick={() => { setOpen(false); setQuery(''); navigate('/quote-dashboard'); }}
                    onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    style={{ padding: '12px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e40af' }}>{d.quote_number}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{d.client_info?.clientName || d.client_info?.name || d.client_info?.attentionName || '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{fmt(d.total_with_vat)} FCFA</div>
                      <span style={{ fontSize: '11px', background: (STATUT_COLOR[d.statut]||'#9ca3af') + '22', color: STATUT_COLOR[d.statut]||'#9ca3af', padding: '2px 8px', borderRadius: '999px', fontWeight: 600 }}>{STATUT_LABEL[d.statut] || d.statut}</span>
                    </div>
                  </div>
                ))}
              </>)}

              {results.clients.length > 0 && (<>
                <div style={{ padding: '8px 20px 6px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>👤 Clients ({results.clients.length})</div>
                {results.clients.map(c => (
                  <div key={c.id} onClick={() => { setOpen(false); setQuery(''); navigate('/quote-dashboard?vue=clients'); }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    style={{ padding: '12px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: '#111827' }}>{c.nom}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{[c.email, c.mobile, c.ville].filter(Boolean).join(' · ')}</div>
                    </div>
                    <span style={{ fontSize: '18px' }}>→</span>
                  </div>
                ))}
              </>)}

              {results.factures.length > 0 && (<>
                <div style={{ padding: '8px 20px 6px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>🧾 Factures ({results.factures.length})</div>
                {results.factures.map(f => (
                  <div key={f.id} onClick={() => { setOpen(false); setQuery(''); navigate('/invoice-dashboard'); }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fffbeb'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    style={{ padding: '12px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: '#d97706' }}>{f.invoice_number}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{fmt(f.montant)} FCFA</div>
                    </div>
                    <span style={{ fontSize: '18px' }}>→</span>
                  </div>
                ))}
              </>)}
            </div>

            {/* Footer */}
            <div style={{ padding: '10px 20px', borderTop: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', gap: '20px', fontSize: '11px', color: '#9ca3af' }}>
              <span>↵ Ouvrir</span><span>Fermer</span>
              {isManager && <span style={{ marginLeft: 'auto', color: '#3b82f6', fontWeight: 600 }}>Recherche globale — tous les utilisateurs</span>}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
